import React, { useState } from 'react';
import PropTypes from 'prop-types';
import MobileLockOverview from './MobileLockOverview';
import MobileStandings from './MobileStandings';
import MobileLeagueOverview from './MobileLeagueOverview';
import MobileLeagueStats from './MobileLeagueStats';
import styles from './MobileLeagueStatsShell.module.css';

const MobileLeagueStatsShell = ({
  selectedSeason,
  effectiveSeason,
  availableSeasons,
  onSeasonChange,
  leaderboardData,
  loading,
  error,
  onRefreshLeaderboard,
}) => {
  const [activeTab, setActiveTab] = useState('live');
  const [liveNavigation, setLiveNavigation] = useState(null);
  const season = selectedSeason || effectiveSeason;
  const isArchivedSeason = season && season < effectiveSeason;

  const handleUserSelect = (username) => {
    setLiveNavigation({ viewMode: 'by-user', username });
    setActiveTab('live');
  };

  const renderContent = () => {
    if (loading && !leaderboardData) {
      return (
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <p>Loading league stats...</p>
        </div>
      );
    }

    if (error) {
      return <div className={styles.error}>{error}</div>;
    }

    switch (activeTab) {
      case 'live':
        return (
          <MobileLockOverview
            season={season}
            leaderboardData={leaderboardData}
            navigationRequest={liveNavigation}
            onNavigationHandled={() => setLiveNavigation(null)}
          />
        );
      case 'standings':
        return (
          <MobileStandings
            season={season}
            leaderboardData={leaderboardData}
            onUserSelect={handleUserSelect}
          />
        );
      case 'overview':
        return (
          <MobileLeagueOverview
            season={season}
            leaderboardData={leaderboardData}
            onRefreshLeaderboard={onRefreshLeaderboard}
          />
        );
      case 'metrics':
        return <MobileLeagueStats season={season} embedded />;
      default:
        return null;
    }
  };

  return (
    <div className={`${styles.shell} ${isArchivedSeason ? styles.archived : ''}`}>
      <div className={styles.header}>
        <h1 className={styles.title}>League Stats</h1>
        <div className={styles.seasonRow}>
          <label htmlFor="mobile-season-select">Season</label>
          <select
            id="mobile-season-select"
            value={season || ''}
            onChange={(e) => onSeasonChange(parseInt(e.target.value, 10))}
            className={isArchivedSeason ? styles.archivedSelect : ''}
          >
            {(availableSeasons.length > 0 ? availableSeasons : [effectiveSeason]).map((s) => (
              <option key={s} value={s}>
                {s === effectiveSeason ? `Current (${s})` : s}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={styles.tabs} role="tablist" aria-label="League stats views">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'live'}
          className={`${styles.tab} ${activeTab === 'live' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('live')}
        >
          Live
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'standings'}
          className={`${styles.tab} ${activeTab === 'standings' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('standings')}
        >
          Standings
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'overview'}
          className={`${styles.tab} ${activeTab === 'overview' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'metrics'}
          className={`${styles.tab} ${activeTab === 'metrics' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('metrics')}
        >
          Metrics
        </button>
      </div>

      <div className={styles.content} role="tabpanel">
        {renderContent()}
      </div>
    </div>
  );
};

MobileLeagueStatsShell.propTypes = {
  selectedSeason: PropTypes.number,
  effectiveSeason: PropTypes.number,
  availableSeasons: PropTypes.arrayOf(PropTypes.number),
  onSeasonChange: PropTypes.func.isRequired,
  leaderboardData: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.string,
  onRefreshLeaderboard: PropTypes.func,
};

export default MobileLeagueStatsShell;
